import { T } from "@repo/transifex";
import { cn } from "~/lib/utils";
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow
} from "~/components/ui/table";

type Column = {
  key: string;
  label: string;
  align?: "left" | "right";
  /** Tailwind width class (e.g. "w-1/2"). Needs table-fixed to take effect. */
  width?: string;
};

type ReportTableSectionProps = {
  title: string;
  columns: Column[];
  rows: Record<string, string | number>[];
  /**
   * Row field to key on. Defaults to the first column, whose text is not always
   * unique — two deposit schemes, or two categories, can share a name.
   */
  rowKey?: string;
  footer?: Record<string, string | number>;
};

export const ReportTableSection = ({
  title,
  columns,
  rows,
  rowKey,
  footer
}: ReportTableSectionProps) => (
  <div className="flex flex-col gap-2">
    <h3 className="text-base font-bold text-text-primary">
      <T _str={title} />
    </h3>
    <div className="overflow-hidden rounded-xl bg-white">
      <Table className="table-fixed">
        <TableHeader>
          <TableRow className="border-b border-divider-main hover:bg-transparent">
            {columns.map((col) => (
              <TableHead
                key={col.key}
                className={cn(
                  "h-10 px-4 text-xs font-semibold text-text-secondary",
                  col.align === "right" && "text-right",
                  col.width
                )}
              >
                <T _str={col.label} />
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => (
            <TableRow
              key={String(row[rowKey ?? columns[0].key])}
              className="border-b border-divider-main last:border-0 hover:bg-transparent"
            >
              {columns.map((col) => (
                <TableCell
                  key={col.key}
                  className={cn(
                    "px-4 py-2.5 text-sm text-text-primary",
                    col.align === "right" && "text-right"
                  )}
                >
                  {row[col.key]}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
        {footer ? (
          <TableFooter className="border-t border-divider-main bg-gray-100">
            <TableRow className="hover:bg-transparent">
              {columns.map((col) => (
                <TableCell
                  key={col.key}
                  className={cn(
                    "px-4 py-2.5 text-sm font-semibold text-text-primary",
                    col.align === "right" && "text-right"
                  )}
                >
                  {footer[col.key]}
                </TableCell>
              ))}
            </TableRow>
          </TableFooter>
        ) : null}
      </Table>
    </div>
  </div>
);
